/* Plant Drop location, availability and minimum-order behaviors.
   Crawler-safe: the server render never filters. After load:
   - IP auto-detect applies a served state automatically (clearly shown, one-tap Change)
   - a confirmed postcode overrides it
   - collection pages use Shopify tag URLs (/collections/x/state-qld) so filtered pages are full
   - sizes stocked nowhere are labelled Backorder and cannot be purchased */
(function () {
  'use strict';

  var SERVED = ['QLD', 'NSW', 'VIC', 'SA', 'ACT', 'WA'];
  var RANGES = {
    NT: [[800, 999]],
    NSW: [[1000, 2599], [2619, 2898]],
    ACT: [[200, 299], [2600, 2618], [2900, 2920]],
    VIC: [[3000, 3999], [8000, 8999]],
    QLD: [[4000, 4999], [9000, 9799]],
    SA: [[5000, 5999]],
    WA: [[6000, 6999]],
    TAS: [[7000, 7999]]
  };
  var MIN_ORDER_CENTS = 39900;

  function postcodeToState(pc) {
    var n = parseInt(pc, 10);
    if (isNaN(n)) return null;
    for (var state in RANGES) {
      var ranges = RANGES[state];
      for (var i = 0; i < ranges.length; i++) {
        if (n >= ranges[i][0] && n <= ranges[i][1]) return state;
      }
    }
    return null;
  }

  function getLoc() {
    /* 28 Sep 2026: location-based availability is retired. Every size sells nationally, so a
       pd_loc left in localStorage from before the August teardown (or from testing) must never
       mark a size backorder or hide a card. It is cleared here and the location is always null. */
    try { window.localStorage.removeItem('pd_loc'); } catch (e) { /* storage unavailable */ }
    return null;
  }

  /* farm geo data: farm coordinates + postcode -> nearby farm zones (75km) */
  var pdGeo = null;
  var pdGeoUrl = null;
  function loadGeo(cb) {
    if (pdGeo) { cb(pdGeo); return; }
    if (!pdGeoUrl) {
      var s = document.querySelector('script[src*="pd-location"]');
      if (s) pdGeoUrl = s.src.replace(/pd-location\.js[^?]*/, 'pd-farm-geo.json').split('?')[0];
    }
    if (!pdGeoUrl) { cb(null); return; }
    fetch(pdGeoUrl)
      .then(function (r) { return r.json(); })
      .then(function (g) { pdGeo = g; cb(g); })
      .catch(function () { cb(null); });
  }

  function havKm(la1, lo1, la2, lo2) {
    var rad = Math.PI / 180;
    var dLa = (la2 - la1) * rad, dLo = (lo2 - lo1) * rad;
    var a = Math.sin(dLa / 2) * Math.sin(dLa / 2) + Math.cos(la1 * rad) * Math.cos(la2 * rad) * Math.sin(dLo / 2) * Math.sin(dLo / 2);
    return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  function farmsNearCoords(geo, lat, lon) {
    var near = [];
    for (var id in geo.farms) {
      var c = geo.farms[id];
      if (havKm(lat, lon, c[0], c[1]) <= geo.radiusKm) near.push(id);
    }
    return near.join(',');
  }

  /* observe the theme's own ajax cart calls so farm data refreshes AFTER a mutation
     commits; fixed timers alone can read the cart before a slow add/change lands.
     Our own attribute POSTs go through pdOrigFetch so they never re-trigger this. */
  var pdOrigFetch = window.fetch ? window.fetch.bind(window) : null;
  if (pdOrigFetch) {
    window.fetch = function () {
      var u = arguments[0];
      if (u && typeof u === 'object') u = u.url || u.href || ''; /* Request or URL instance */
      var p = pdOrigFetch.apply(null, arguments);
      if (typeof u === 'string' && /\/cart\/(add|change|update|clear)/.test(u)) {
        p.then(function (r) { if (r && r.ok) scheduleFarmRefresh(250); }, function () {});
      }
      return p;
    };
  }

  var farmRefreshTimer;
  function scheduleFarmRefresh(delayMs) {
    clearTimeout(farmRefreshTimer);
    farmRefreshTimer = setTimeout(function () { applyCartGate(); refreshFarmIntelligence(); }, delayMs);
  }

  /* identical-string textContent writes still queue childList mutation records, which
     would re-fire the MutationObserver (and its /cart.js fetch) forever - so every
     write reachable from the observer only happens when the value actually changed */
  function setText(el, s) {
    if (el && el.textContent !== s) el.textContent = s;
  }

  function setLoc(pc, state, detected, farms) {
    try { window.localStorage.setItem('pd_loc', JSON.stringify({ pc: pc, state: state, detected: !!detected, farms: farms || '' })); } catch (e) { /* ignore */ }
    syncCartAttributes(pc, state);
  }

  function clearLoc() {
    try { window.localStorage.removeItem('pd_loc'); } catch (e) { /* ignore */ }
    syncCartAttributes('', '');
  }

  function syncCartAttributes(pc, state) {
    try {
      (pdOrigFetch || window.fetch)((window.routes && window.routes.cart_update_url) || '/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { pd_postcode: pc, pd_state: state } })
      }).catch(function () {});
    } catch (e) { /* ignore */ }
  }

  /* stamp the farms that can fill the whole cart onto the order (visible in admin
     under Additional details as pd_farms); only POSTs when the value changed */
  var pdFarmStampSeq = 0;
  function syncFarmAttributes(data) {
    if (!data || typeof data.itemCount === 'undefined') return;
    var seq = ++pdFarmStampSeq;
    var current = data.attrFarms || '';
    var stamp = function (val) {
      if (seq !== pdFarmStampSeq) return; /* a newer cart snapshot superseded this one */
      if (val === current) return;
      try {
        (pdOrigFetch || window.fetch)((window.routes && window.routes.cart_update_url) || '/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: { pd_farms: val } })
        }).catch(function () {});
      } catch (e) { /* ignore */ }
    };
    if (!data.itemCount) { stamp(''); return; }
    if (!data.farmDataComplete) { stamp('Check farms per item - some cart lines have no farm data'); return; }
    var ids = splitCsv(data.farms);
    if (!ids.length) { stamp('SPLIT ORDER - no single partner farm stocks every item'); return; }
    loadGeo(function (g) {
      var names = ids.map(function (id) {
        return (g && g.names && g.names[id]) ? (g.names[id] + ' [' + id + ']') : id;
      });
      var val = names.slice(0, 8).join('; ');
      if (names.length > 8) val += '; +' + (names.length - 8) + ' more';
      stamp(val);
    });
  }

  /* ---------- sticky bar (compact row + tap-to-expand panel) ---------- */
  var bar = document.getElementById('pd-locbar');

  function setPanel(open, noFocus) {
    if (!bar) return;
    var panel = bar.querySelector('[data-pd-loc-panel]');
    var toggle = bar.querySelector('[data-pd-loc-toggle]');
    if (!panel || !toggle) return;
    panel.hidden = !open;
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (open) { bar.setAttribute('data-open', ''); if (!noFocus) { var i = panel.querySelector('input'); if (i) i.focus(); } }
    else { bar.removeAttribute('data-open'); }
  }

  function renderBar() {
    if (!bar) return;
    var label = bar.querySelector('[data-pd-loc-label]');
    var err = bar.querySelector('[data-pd-loc-error]');
    var clear = bar.querySelector('[data-pd-loc-clear]');
    var loc = getLoc();
    if (err) err.hidden = true;
    if (loc) {
      if (SERVED.indexOf(loc.state) >= 0) {
        if (loc.farms) {
          label.textContent = (loc.pc || loc.state) + ' · farms near you' + (loc.detected && !loc.pc ? ' (detected)' : '');
        } else {
          label.textContent = (loc.pc ? loc.pc + ' · ' : '') + loc.state + '-wide' + (loc.detected && !loc.pc ? ' (detected)' : '');
        }
      } else {
        label.textContent = 'No delivery to ' + loc.state + ' yet';
      }
      if (clear) clear.hidden = false;
    } else {
      label.textContent = 'Set your location';
      if (clear) clear.hidden = true;
    }
  }

  /* IP auto-detect: applies served states automatically; anything else stays national */
  function autoDetectFromIp() {
    if (getLoc()) return;
    var flagged;
    try { flagged = window.sessionStorage.getItem('pd_ip_prompted'); } catch (e) { return; }
    if (flagged) return;
    try { window.sessionStorage.setItem('pd_ip_prompted', '1'); } catch (e) { /* ignore */ }
    var REGION_MAP = {
      'Queensland': 'QLD', 'New South Wales': 'NSW', 'Victoria': 'VIC', 'South Australia': 'SA',
      'Australian Capital Territory': 'ACT', 'Western Australia': 'WA', 'Tasmania': 'TAS', 'Northern Territory': 'NT'
    };
    var controller = ('AbortController' in window) ? new AbortController() : null;
    if (controller) setTimeout(function () { controller.abort(); }, 3000);
    fetch('https://get.geojs.io/v1/ip/geo.json', controller ? { signal: controller.signal } : {})
      .then(function (r) { return r.json(); })
      .then(function (geo) {
        if (getLoc()) return;
        if (!geo || geo.country_code !== 'AU') return;
        var state = REGION_MAP[geo.region];
        if (!state || SERVED.indexOf(state) < 0) return;
        var lat = parseFloat(geo.latitude), lon = parseFloat(geo.longitude);
        loadGeo(function (g) {
          var farms = (g && !isNaN(lat) && !isNaN(lon)) ? farmsNearCoords(g, lat, lon) : '';
          setLoc('', state, true, farms);
          renderBar();
          applyAll();
        });
      })
      .catch(function () {});
  }

  if (bar) {
    var form = bar.querySelector('[data-pd-loc-form]');
    var toggle = bar.querySelector('[data-pd-loc-toggle]');
    var clear = bar.querySelector('[data-pd-loc-clear]');
    var err = bar.querySelector('[data-pd-loc-error]');
    toggle.addEventListener('click', function () {
      var panel = bar.querySelector('[data-pd-loc-panel]');
      setPanel(panel.hidden);
    });
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var input = form.querySelector('input');
      var pc = (input.value || '').trim();
      var state = /^\d{4}$/.test(pc) ? postcodeToState(pc) : null;
      if (!state) {
        err.textContent = 'Enter a valid Australian postcode';
        err.hidden = false;
        return;
      }
      loadGeo(function (g) {
        var farms = (g && g.zones && g.zones[pc]) ? g.zones[pc] : '';
        setLoc(pc, state, false, farms);
        renderBar();
        setPanel(false);
        applyAll();
      });
    });
    if (clear) {
      clear.addEventListener('click', function () {
        clearLoc();
        renderBar();
        setPanel(false);
        applyAll();
      });
    }
    /* the sheet floats over the page, so close it when tapping anywhere else */
    document.addEventListener('click', function (e) {
      var panel = bar.querySelector('[data-pd-loc-panel]');
      if (panel && !panel.hidden && !bar.contains(e.target)) setPanel(false);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      var panel = bar.querySelector('[data-pd-loc-panel]');
      if (panel && !panel.hidden) setPanel(false);
    });
  }

  /* ---------- collection pages: server-side filtering via tag URLs ---------- */
  function applyCollectionUrl(loc) {
    var m = window.location.pathname.match(/^\/collections\/([^\/]+)(?:\/([^\/]+))?\/?$/);
    if (!m) return false;
    var handle = m[1];
    var currentTag = m[2] || '';
    if (currentTag.indexOf('state-') !== 0 && currentTag !== '') return false; /* leave other tag pages alone */
    var desired = (loc && SERVED.indexOf(loc.state) >= 0) ? 'state-' + loc.state.toLowerCase() : '';
    if (currentTag === desired) return false;
    var target = '/collections/' + handle + (desired ? '/' + desired : '') + window.location.search;
    window.location.replace(target);
    return true;
  }

  /* locations stored before the farm-geo layer shipped have no farm list; upgrade them */
  function migrateLoc() {
    var loc = getLoc();
    if (!loc || loc.farms || SERVED.indexOf(loc.state) < 0) return;
    loadGeo(function (g) {
      if (!g) return;
      if (loc.pc && g.zones && g.zones[loc.pc]) {
        setLoc(loc.pc, loc.state, loc.detected, g.zones[loc.pc]);
        renderBar();
        applyAll();
      } else if (loc.detected && !loc.pc) {
        try { window.sessionStorage.removeItem('pd_ip_prompted'); } catch (e) { /* ignore */ }
        try { window.localStorage.removeItem('pd_loc'); } catch (e) { /* ignore */ }
        autoDetectFromIp();
      }
    });
  }

  /* ---------- grid backfill: filtered pages pull stock from following pages so
     the visitor always sees a full page of what they CAN buy ---------- */
  var pdBackfill = { key: '', nextPage: 0, fetching: false, exhausted: false };

  function backfillKey(loc) {
    return (loc ? (loc.farms || loc.state || '') : '') + '|' + (pdCartFarms && pdCartFarms.farmCount > 0 ? pdCartFarms.farms : '') + '|' + window.location.pathname;
  }

  function visibleCardCount(grid) {
    var cards = grid.querySelectorAll('.grid__item');
    var n = 0;
    for (var i = 0; i < cards.length; i++) {
      if (!cards[i].classList.contains('pd-hidden-by-state') && !cards[i].classList.contains('pd-cart-mismatch') && !cards[i].classList.contains('pd-grid-backorder')) n++;
    }
    return n;
  }

  function backfillIfNeeded() {
    var grid = document.getElementById('product-grid');
    if (!grid || pdBackfill.fetching) return;
    var loc = getLoc();
    var filtering = (loc && (loc.farms || SERVED.indexOf(loc.state) < 0)) ||
      grid.querySelector('.pd-hidden-by-state, .pd-cart-mismatch');
    if (!filtering) return;
    var key = backfillKey(loc);
    if (key !== pdBackfill.key) {
      pdBackfill = { key: key, nextPage: 2, fetching: false, exhausted: false };
      var m = window.location.search.match(/[?&]page=(\d+)/);
      if (m) pdBackfill.nextPage = parseInt(m[1], 10) + 1;
    }
    if (pdBackfill.exhausted || pdBackfill.nextPage > 20) { finishBackfill(grid); return; }
    if (visibleCardCount(grid) >= 24) return;
    pdBackfill.fetching = true;
    var url = new URL(window.location.href);
    url.searchParams.set('page', pdBackfill.nextPage);
    fetch(url.toString())
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var items = doc.querySelectorAll('#product-grid .grid__item');
        if (!items.length) {
          pdBackfill.exhausted = true;
          pdBackfill.fetching = false;
          finishBackfill(grid);
          return;
        }
        for (var i = 0; i < items.length; i++) grid.appendChild(document.importNode(items[i], true));
        pdBackfill.nextPage++;
        pdBackfill.fetching = false;
        /* appended items get filtered by the observer cycle; keep pagination hidden while stitched */
        hidePagination(true);
        applyGridFilter();
        applyGridCartFit();
      })
      .catch(function () { pdBackfill.fetching = false; pdBackfill.exhausted = true; });
  }

  function hidePagination(hide) {
    var pag = document.querySelectorAll('.pagination, .pagination-wrapper, nav[aria-label*="agination"], [class*="pagination"]');
    for (var i = 0; i < pag.length; i++) pag[i].style.display = hide ? 'none' : '';
  }

  function finishBackfill(grid) {
    if (pdBackfill.exhausted && !grid.getAttribute('data-pd-backfill-done')) {
      grid.setAttribute('data-pd-backfill-done', '1');
    }
  }

  /* ---------- client-side grid fallback (search results and unfiltered grids) ---------- */
  function applyGridFilter() {
    var loc = getLoc();
    var onTagFiltered = /^\/collections\/[^\/]+\/state-/.test(window.location.pathname);
    var cards = document.querySelectorAll('[data-pd-states]');
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      var states = card.getAttribute('data-pd-states') || '';
      var farms = card.getAttribute('data-pd-farms') || '';
      var hide = false;
      var backorder = false;
      if (loc && SERVED.indexOf(loc.state) < 0 && !onTagFiltered) {
        hide = true; /* TAS/NT: nothing deliverable at all */
      } else if (loc && loc.farms && farms) {
        /* not stocked by farms near the customer: shown as backorder, not hidden */
        backorder = intersectCsv(farms, loc.farms).length === 0;
      } else if (!onTagFiltered && loc && SERVED.indexOf(loc.state) >= 0 && states) {
        backorder = states.split(',').indexOf(loc.state) < 0;
      }
      card.classList.toggle('pd-hidden-by-state', hide);
      card.classList.toggle('pd-grid-backorder', backorder);
    }
  }

  /* ---------- PDP guard: product state, per-size state, backorder ---------- */
  /* Backorder-for-you sizes live in a click-to-open drawer under the size grid.
     Buttons are MOVED (never cloned) so their listeners survive, and they move BACK
     to the main row when a location change makes them orderable again. */
  function decorateBackorderButtons() {
    var buttons = document.querySelectorAll('[data-option-button][data-pd-vstates]');
    if (!buttons.length) return;
    var grid = document.querySelector('[data-option-group]');
    var backorderButtons = [];
    for (var i = 0; i < buttons.length; i++) {
      var b = buttons[i];
      if (b.classList.contains('pd-size-backorder')) {
        if (!b.querySelector('.pd-bo-badge')) {
          var badge = document.createElement('span');
          badge.className = 'pd-bo-badge';
          badge.textContent = 'backorder';
          b.appendChild(badge);
        }
        backorderButtons.push(b);
      } else {
        var old = b.querySelector('.pd-bo-badge');
        if (old) old.remove();
        if (grid && b.parentElement !== grid) grid.appendChild(b); /* back to the main row */
      }
    }
    var list = document.querySelector('[data-pd-backorder-list]');
    if (list) list.hidden = true; /* superseded by the drawer */
    var drawer = document.querySelector('[data-pd-backorder-drawer]');
    if (!backorderButtons.length) {
      if (drawer) drawer.style.display = 'none';
      return;
    }
    if (!drawer && grid) {
      drawer = document.createElement('details');
      drawer.className = 'pd-backorder-drawer';
      drawer.setAttribute('data-pd-backorder-drawer', '');
      var summary = document.createElement('summary');
      summary.className = 'pd-backorder-drawer__summary';
      drawer.appendChild(summary);
      var box = document.createElement('div');
      box.className = 'pd-backorder-drawer__sizes';
      drawer.appendChild(box);
      var hint = document.createElement('p');
      hint.className = 'pd-backorder-drawer__hint';
      hint.textContent = 'These sizes are not available from partner farms for your area right now. Select one for the email notify option - availability updates daily.';
      drawer.appendChild(hint);
      grid.parentElement.insertBefore(drawer, grid.nextSibling);
    }
    if (drawer) {
      drawer.style.display = '';
      var box2 = drawer.querySelector('.pd-backorder-drawer__sizes');
      for (var j = 0; j < backorderButtons.length; j++) {
        if (backorderButtons[j].parentElement !== box2) box2.appendChild(backorderButtons[j]);
      }
      var sum = drawer.querySelector('.pd-backorder-drawer__summary');
      setText(sum, 'Sizes on backorder (' + backorderButtons.length + ')');
    }
  }

  /* a size is "backorder for you" when NO fresh farm has it (NONE) OR when no farm
     near you / in your state stocks it - either way: in the drawer with the notify option */
  function applySizeFilter(loc) {
    var sizeButtons = document.querySelectorAll('[data-option-button][data-pd-vstates]');
    if (!sizeButtons.length) return { filtered: false, anyVisible: true, activeBackorder: false };
    var firstBuyable = null;
    var anyVisible = false;
    var activeMoved = false;
    for (var i = 0; i < sizeButtons.length; i++) {
      var b = sizeButtons[i];
      var vs = b.getAttribute('data-pd-vstates') || '';
      var vf = b.getAttribute('data-pd-vfarms') || '';
      var backorder = (vs === 'NONE');
      if (!backorder && loc && SERVED.indexOf(loc.state) >= 0) {
        if (loc.farms && vf && vf !== 'NONE') {
          backorder = intersectCsv(vf, loc.farms).length === 0;
        } else if (vs) {
          backorder = vs.split(',').indexOf(loc.state) < 0;
        }
      }
      b.classList.toggle('pd-size-backorder', backorder);
      b.classList.remove('pd-hidden-size');
      if (!backorder) {
        anyVisible = true;
        if (!firstBuyable) firstBuyable = b;
      } else if (b.classList.contains('is-active')) {
        activeMoved = true;
      }
    }
    decorateBackorderButtons();
    /* if the selection just became backorder due to a location change, prefer a buyable size */
    if (activeMoved && firstBuyable && !document.querySelector('[data-pd-backorder-drawer][open]')) {
      firstBuyable.click();
    }
    var active = document.querySelector('[data-option-button].is-active');
    var activeBackorder = !!(active && active.classList.contains('pd-size-backorder'));
    return { filtered: true, anyVisible: anyVisible, activeBackorder: activeBackorder };
  }

  var pdNotifyDone = /customer_posted=true/.test(window.location.search);

  function applyPdpGuard() {
    var holder = document.querySelector('[data-pd-product-states]');
    if (!holder) return;
    var loc = getLoc();
    var states = holder.getAttribute('data-pd-product-states') || '';
    var farms = holder.getAttribute('data-pd-product-farms') || '';
    var notice = document.querySelector('[data-pd-state-notice]');
    var buttons = document.querySelectorAll('[data-add-button], [data-mobile-atc]');
    var sizes = applySizeFilter(loc);
    var blocked = false;
    var msg = '';
    if (loc && SERVED.indexOf(loc.state) < 0) {
      blocked = true;
      msg = 'We do not deliver to ' + loc.state + ' yet.';
    }
    var notify = document.querySelector('[data-pd-backorder-notify]');
    if (!blocked && sizes.filtered && !sizes.anyVisible && !sizes.activeBackorder) {
      blocked = true;
      msg = 'Every size of this plant is on backorder right now. Open "Sizes on backorder" below and pick one for the email notify option.';
    }
    if (!blocked && sizes.activeBackorder) {
      blocked = true;
      msg = 'This size is on backorder right now. Availability updates daily.';
    }
    /* whenever a backorder size is the active selection, offer the email-notify form;
       the hidden tags field records exactly which plant, size and postcode to chase */
    if (pdNotifyDone && notify) {
      notify.hidden = false; /* keep the signup confirmation visible */
    } else if (sizes.activeBackorder && notify) {
      var active = document.querySelector('[data-option-button].is-active');
      var sizeName = active ? active.getAttribute('data-option-value') : '';
      var tagsInput = notify.querySelector('[data-pd-notify-tags]');
      if (tagsInput) {
        var handle = (window.location.pathname.split('/products/')[1] || '').split('/')[0].split('?')[0];
        var tags = 'back-in-stock,BIS:' + handle + ':' + sizeName;
        var loc0 = getLoc();
        if (loc0 && loc0.pc) tags += ',BISPC:' + loc0.pc;
        tagsInput.value = tags;
      }
      notify.hidden = false;
    } else if (notify) {
      notify.hidden = true;
    }
    for (var i = 0; i < buttons.length; i++) {
      buttons[i].disabled = blocked;
      buttons[i].classList.toggle('pd-blocked-by-state', blocked);
    }
    if (notice) {
      setText(notice, msg);
      notice.hidden = !blocked;
    }
  }

  /* ---------- cart minimum gate (the shipping rate is the hard wall) ---------- */
  function applyCartGate() {
    var meters = document.querySelectorAll('[data-pd-min-meter]');
    var checkouts = document.querySelectorAll('[name="checkout"]');
    if (!meters.length && !checkouts.length) return;
    fetch('/cart.js').then(function (r) { return r.json(); }).then(function (cart) {
      applyGateState(cart.total_price);
    }).catch(function () {});
  }

  /* the checkout wall: the $399 minimum only.
     The split-cart block was removed 19 Aug 2026 - the catalogue sells nationally and an order
     may be filled by more than one partner farm. This also unblocks the ~1,198 variants whose
     supply.farms is the literal string NONE, which previously disabled checkout even in a
     single-line cart. */
  function applyGateState(total) {
    var meters = document.querySelectorAll('[data-pd-min-meter]');
    var remaining = MIN_ORDER_CENTS - total;
    var pct = Math.max(0, Math.min(100, Math.round((total / MIN_ORDER_CENTS) * 100)));
    for (var i = 0; i < meters.length; i++) {
      var meter = meters[i];
      var fill = meter.querySelector('[data-pd-min-fill]');
      var text = meter.querySelector('[data-pd-min-text]');
      if (fill) fill.style.width = pct + '%';
      meter.classList.toggle('pd-min-met', remaining <= 0);
      setText(text, remaining > 0
        ? 'Add $' + (remaining / 100).toFixed(2).replace(/\.00$/, '') + ' more to reach the $399 minimum order.'
        : 'Minimum order met. Ships with flat $99 farm-direct delivery.');
    }
    var blocked = remaining > 0;
    var reason = 'Minimum order is $399';
    var checkouts = document.querySelectorAll('[name="checkout"]');
    for (var j = 0; j < checkouts.length; j++) {
      checkouts[j].disabled = blocked;
      checkouts[j].classList.toggle('pd-under-min', blocked);
      if (blocked) checkouts[j].setAttribute('title', reason);
      else checkouts[j].removeAttribute('title');
    }
  }

  /* ---------- farm-anchored cart intelligence ----------
     One partner farm ships each order. The cart's remaining farm set comes from a JSON
     view (/cart?view=pd-farms); PDPs and grids show what still fits that set. */
  var pdCartFarms = null;

  function splitCsv(s) { return (s || '').split(',').filter(function (x) { return x; }); }
  function intersectCsv(aCsv, bCsv) {
    var b = splitCsv(bCsv);
    return splitCsv(aCsv).filter(function (x) { return b.indexOf(x) >= 0; });
  }

  var pdCartFarmsSeq = 0;
  function fetchCartFarms(cb) {
    /* no Accept header: with Accept json Shopify returns the standard cart payload and ignores the view */
    var seq = ++pdCartFarmsSeq;
    fetch('/cart?view=pd-farms')
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (seq !== pdCartFarmsSeq) { if (cb) cb(null); return; } /* a newer request superseded this one */
        pdCartFarms = data;
        syncFarmAttributes(data);
        if (cb) cb(data);
      })
      .catch(function () { if (cb) cb(null); });
  }

  function cartAnchorActive() {
    return !!(pdCartFarms && pdCartFarms.itemCount > 0 && pdCartFarms.farmDataComplete);
  }

  function applyCartFit() {
    var el = document.querySelector('[data-pd-cart-fit]');
    if (!el) return;
    if (!cartAnchorActive() || pdCartFarms.farmCount === 0) { el.hidden = true; return; }
    var active = document.querySelector('[data-option-button].is-active');
    var vfarms = active ? (active.getAttribute('data-pd-vfarms') || '') : '';
    if (!vfarms || vfarms === 'NONE') { el.hidden = true; return; }
    if (intersectCsv(vfarms, pdCartFarms.farms).length) {
      el.className = 'pd-cart-fit pd-cart-fit--ok';
      setText(el, 'Ships together with your cart items - the same partner farm can supply everything.');
    } else {
      el.className = 'pd-cart-fit pd-cart-fit--warn';
      setText(el, 'Heads up: this size ships from a different partner farm than your cart items, so an additional $99 delivery fee would apply. Order it separately to keep things simple.');
    }
    el.hidden = false;
  }

  function applyGridCartFit() {
    var cards = document.querySelectorAll('[data-pd-farms]');
    if (!cards.length) return;
    var active = cartAnchorActive() && pdCartFarms.farmCount > 0;
    var hiddenCount = 0;
    var grid = null;
    for (var i = 0; i < cards.length; i++) {
      var mismatch = false;
      if (active) {
        mismatch = intersectCsv(cards[i].getAttribute('data-pd-farms'), pdCartFarms.farms).length === 0;
      }
      cards[i].classList.toggle('pd-cart-mismatch', mismatch);
      if (mismatch) { hiddenCount++; if (!grid) grid = cards[i].closest('#product-grid, .product-grid') || cards[i].parentElement; }
    }
    /* opt-in reveal, session-scoped */
    var showAnyway = false;
    try { showAnyway = window.sessionStorage.getItem('pd_show_mismatch') === '1'; } catch (e) { /* ignore */ }
    document.documentElement.classList.toggle('pd-show-mismatch', showAnyway);
    /* explain what is hidden and offer the toggle */
    var host = grid ? grid.parentElement : null;
    var notice = document.querySelector('.pd-cart-filter-notice');
    if (hiddenCount > 0 && host) {
      if (!notice) {
        notice = document.createElement('div');
        notice.className = 'pd-cart-filter-notice';
        notice.setAttribute('role', 'status');
        host.insertBefore(notice, grid);
      }
      /* rebuild only when the message would change, so repeat passes queue no mutations */
      var noticeKey = hiddenCount + ':' + (showAnyway ? '1' : '0');
      if (notice.getAttribute('data-pd-key') !== noticeKey) {
        notice.setAttribute('data-pd-key', noticeKey);
        notice.innerHTML = '';
        notice.appendChild(document.createTextNode(
          showAnyway
            ? hiddenCount + ' plant' + (hiddenCount === 1 ? '' : 's') + ' shown dimmed ship from a different farm than your cart, so an additional $99 delivery fee would apply.'
            : hiddenCount + ' plant' + (hiddenCount === 1 ? ' is' : 's are') + ' hidden because they ship from a different farm than the items in your cart and would need their own $99 delivery.'
        ));
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.textContent = showAnyway ? 'Hide them' : 'Show them anyway';
        btn.addEventListener('click', function () {
          try { window.sessionStorage.setItem('pd_show_mismatch', showAnyway ? '0' : '1'); } catch (e) { /* ignore */ }
          applyGridCartFit();
        });
        notice.appendChild(btn);
      }
      notice.hidden = false;
    } else if (notice) {
      notice.hidden = true;
    }
  }

  function refreshFarmPanel() {
    var panel = document.querySelector('[data-pd-farm-anchor]');
    if (!panel || !cartAnchorActive()) return;
    var n = pdCartFarms.farmCount;
    var mode = n > 1 ? 'multi' : (n === 1 ? 'one' : 'none');
    if (panel.getAttribute('data-pd-mode') === mode) return; /* unchanged: queue no mutations */
    panel.setAttribute('data-pd-mode', mode);
    if (n > 1) {
      panel.className = 'pd-farm-anchor pd-farm-anchor--ok';
      panel.innerHTML = '<span class="pd-farm-anchor__states">Multiple partner farms</span> can fill this whole order, so everything ships together for one flat $99 delivery.';
    } else if (n === 1) {
      panel.className = 'pd-farm-anchor pd-farm-anchor--ok';
      panel.innerHTML = '<span class="pd-farm-anchor__states">One partner farm</span> can fill this whole order. Everything ships together for one flat $99 delivery, but adding plants this farm does not stock will split the order.';
    }
    /* n === 0: keep the server-rendered item breakdown */
  }

  /* Farm data is still fetched, but only so syncFarmAttributes can stamp pd_farms onto the
     order for ops routing (Get-OrderFarms.ps1). It no longer dims grid cards, warns on the
     PDP or gates checkout. */
  function refreshFarmIntelligence() {
    fetchCartFarms(function () {
      if (pdCartFarms && typeof pdCartFarms.totalCents === 'number') applyGateState(pdCartFarms.totalCents);
    });
  }

  /* Location-based browsing was removed 19 Aug 2026. No state redirect, no grid filtering,
     no card dimming: every visitor sees the whole catalogue. applyPdpGuard is retained only
     for genuine backorder sizes (supply.states === 'NONE' means no farm anywhere stocks it),
     which is a stock fact rather than a location rule. */
  function applyAll() {
    applyPdpGuard();
    applyCartGate();
    refreshFarmIntelligence();
  }

  var debounce;
  var observer = new MutationObserver(function () {
    clearTimeout(debounce);
    debounce = setTimeout(function () {
      applyPdpGuard();
      applyCartGate();
    }, 120);
  });

  document.addEventListener('DOMContentLoaded', function () {
    applyAll();
    /* back from a notify signup: Shopify redirects here with customer_posted=true */
    if (/customer_posted=true/.test(window.location.search)) {
      var notifyBox = document.querySelector('[data-pd-backorder-notify]');
      if (notifyBox) {
        var nForm = notifyBox.querySelector('form');
        var nOk = notifyBox.querySelector('[data-pd-notify-success]');
        if (nForm) nForm.hidden = true;
        if (nOk) nOk.hidden = false;
        notifyBox.hidden = false;
      }
    }
    var main = document.getElementById('MainContent');
    if (main) observer.observe(main, { childList: true, subtree: true });
    document.addEventListener('submit', function (e) {
      if (e.target && e.target.closest && e.target.closest('form[action*="/cart"]')) {
        scheduleFarmRefresh(800);
      }
    }, true);
    document.addEventListener('click', function (e) {
      if (e.target && e.target.closest && e.target.closest('[data-option-button]')) {
        setTimeout(function () { applyPdpGuard(); }, 180);
      }
      /* any add-to-cart or cart quantity button: refresh the farm picture shortly after
         (fallback only - the fetch hook fires the reliable refresh when the mutation commits) */
      if (e.target && e.target.closest && e.target.closest('[data-add-button], [data-mobile-atc], [data-qty-plus], [data-qty-minus], [name="plus"], [name="minus"], .quantity__button, [data-cart-remove], cart-remove-button')) {
        scheduleFarmRefresh(1200);
      }
    }, true);
  });
})();
